import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { DashboardLayout } from "@/pages/Dashboard";
import TableWithSearch from "@/components/TableWithSearch";
import FilterDropdown from "@/components/FilterDropDowns";
import type { ColumnHeader } from "@/components/MyTable";
import { formatDateTime12 } from "@/utils/helpers";
import { PatientInfoCard } from "../components/PatientInfoCard";
import {
  getLegacyDateRangeParams,
  getLegacyMonthFilters,
  LEGACY_DEFAULT_HIDDEN_COLUMN_IDS,
  LEGACY_MONTH_FILTER_OPTIONS,
} from "../legacyTableDefaults";

const nursingNoteHeaders: ColumnHeader[] = [
  { id: "sourcePatientId", label: "ID", isSortable: false },
  { id: "patientName", label: "Patient Name", isSortable: false },
  { id: "dob", label: "DOB", isSortable: false },
  { id: "icdCodes", label: "ICD Codes", isSortable: false },
  { id: "primaryInsurance", label: "Primary Insurance", isSortable: false },
  { id: "insuranceId", label: "Insurance ID", isSortable: false },
  { id: "address", label: "Address", isSortable: false },
  { id: "program", label: "Program", isSortable: false },
  { id: "careType", label: "Care Type", isSortable: false },
  { id: "dateTime", label: "Date & Time", isSortable: false },
  { id: "duration", label: "Duration", isSortable: false },
  { id: "note", label: "Note", isSortable: false },
];

function formatIcdCodes(value: unknown) {
  const formatted = Array.isArray(value) ? value.join(", ") : value || "";
  return String(formatted).replace(/~/g, ", ");
}

function formatLegacyDateTime(value: unknown) {
  if (!value) return "";

  const formatted = formatDateTime12(value as string | number | Date);
  return formatted || String(value);
}

const EMPTY_FILTERS = {};
const NOOP = () => {};

function mapNursingNoteRows(rows: any[]) {
  return rows.map((note: any) => ({
    ...note,
    sourcePatientId: note?.sourcePatientId || note?.patientId || note?.patientID || "",
    patientName: note?.patientName || note?.name || "",
    dob: note?.dob || note?.dateOfBirth || "",
    icdCodes: formatIcdCodes(note?.icdCodes || note?.icdCode),
    primaryInsurance: note?.primaryInsurance || note?.primaryInsuranceName || "",
    insuranceId:
      note?.insuranceId ||
      note?.insuranceID ||
      note?.primaryInsuranceNumber ||
      "",
    address:
      note?.address ||
      [note?.street, note?.city, note?.state, note?.zipCode].filter(Boolean).join(", "),
    careType: Array.isArray(note?.careType)
      ? note.careType.join(", ")
      : note?.careType || "",
    dateTime: formatLegacyDateTime(
      note?.dateTime || note?.createdAt || note?.noteDate || note?.date,
    ),
    note: (
      <div className="w-[460px] whitespace-normal wrap-break-word leading-relaxed">
        {note?.note || note?.notes || note?.content || ""}
      </div>
    ),
    duration: note?.duration ? `${note.duration} min` : "",
  }));
}

export default function LegacyNursingNotes() {
  const { patientId } = useParams();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState<Record<string, string>>(() => getLegacyMonthFilters());
  const requestParams = useMemo(
    () => ({
      patientId,
      ...getLegacyDateRangeParams(filters.month),
    }),
    [filters.month, patientId],
  );
  const viewData = useMemo(() => mapNursingNoteRows(rows), [rows]);

  return (
    <DashboardLayout activePage="/legacy">
      <div className="space-y-4">
        <PatientInfoCard />
        <TableWithSearch
          heading="Past Medical Records Nursing Notes"
          url="/legacy-nursing-notes"
          params={requestParams}
          apiDataKey="notes"
          fetchCondition={!!patientId}
          universalLoadingSkeleton
          isIncludeSearch={false}
          searchPlaceholder="Search nursing notes"
          headerExtraControls={
            <FilterDropdown
              filters={filters}
              setFilters={setFilters}
              filtersOptions={LEGACY_MONTH_FILTER_OPTIONS}
              defaultFilters={getLegacyMonthFilters()}
              isHideClearAllFilters
            />
          }
          columnHeaders={nursingNoteHeaders}
          data={rows}
          viewData={viewData}
          setData={setRows}
          isActionColumn={false}
          filtersOptions={[]}
          handleAction={NOOP}
          filters={EMPTY_FILTERS}
          setFilters={NOOP}
          clickRow={NOOP}
          isColumnSelection
          columnPreferenceKey="legacy-nursing-notes-v5"
          defaultHiddenColumnIds={LEGACY_DEFAULT_HIDDEN_COLUMN_IDS}
          isIgnoreSearchParams
        />
      </div>
    </DashboardLayout>
  );
}
